import React from "react";
import { motion, HTMLMotionProps } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { logAnalyticsEvent } from "../lib/firebase";

interface SpecialRole {
  name: string;
  description: string;
  multiplier: string;
  diceNumbers: number[];
  isNegative?: boolean;
}

interface DiceRoleProps {
  numbers: number[];
  description: string;
}

const DiceRole: React.FC<DiceRoleProps> = ({ numbers, description }) => (
  <div className="flex items-center space-x-2 mb-2">
    {numbers.map((num) => (
      <img
        key={num}
        src={`/images/dice${num}.png`}
        alt={`サイコロの目${num}`}
        className="w-8 h-8"
      />
    ))}
    <span className="text-gray-300 text-sm">{description}</span>
  </div>
);

const MotionDiv = motion.div as React.FC<HTMLMotionProps<"div">>;

const Rules: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const specialRoles: SpecialRole[] = [
    {
      name: "ピンゾロ",
      description: "1-1-1の目。最強の役で、高額配当が得られます。",
      multiplier: "×5",
      diceNumbers: [1, 1, 1],
    },
    {
      name: "アラシ",
      description: "同じ目が3つ揃った状態（1-1-1を除く）。3倍勝ち",
      multiplier: "×3",
      diceNumbers: [4, 4, 4],
    },
    {
      name: "シゴロ",
      description: "4-5-6の目。2倍勝ち",
      multiplier: "×2",
      diceNumbers: [4, 5, 6],
    },
    {
      name: "ヒフミ",
      description: "1-2-3の目。不運な役で-2倍負け",
      multiplier: "×-2",
      diceNumbers: [1, 2, 3],
      isNegative: true,
    },
  ];

  const handleSpecialRoleView = (roleName: string) => {
    logAnalyticsEvent("special_role_view", {
      role_name: roleName,
    });
  };

  return (
    <section
      id="rules"
      className="py-20 bg-gradient-to-b from-black-face/50 to-transparent"
    >
      <div className="container">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title text-center">ゲームルール</h2>
          <div className="max-w-3xl mx-auto">
            <div className="card mb-8">
              <h3 className="text-2xl font-bold mb-4">基本ルール</h3>
              <p className="text-gray-300 mb-6">
                3つのサイコロを振って、より強い目を出すゲームです。
                親から子へと順番に対戦していき、配当を競い合います。
                特殊役が出ると、賭け金に倍率が掛かります。
              </p>
              <div className="grid grid-cols-2 gap-4">
                <DiceRole numbers={[1, 2, 3]} description="通常の目" />
                <DiceRole numbers={[4, 5, 6]} description="高い目" />
              </div>
            </div>

            <div className="card mb-8">
              <h3 className="text-2xl font-bold mb-4">対戦モードの流れ</h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-300 mb-6">
                <li>賭け額を決める</li>
                <li>CPUがサイコロを振る</li>
                <li>プレイヤーがサイコロを振る</li>
                <li>勝敗が決まる</li>
                <li>結果に応じて賭けた通貨が増減</li>
              </ol>
              <p className="text-sm text-gray-400">
                ※ 同じ結果の場合は親の勝利になります
              </p>
            </div>

            <h3 className="text-2xl font-bold mb-6 flex items-center">
              <SparklesIcon className="w-6 h-6 text-accent mr-2" />
              特殊役
            </h3>
            <div className="grid md:grid-cols-3 gap-6">
              {specialRoles.map((role) => (
                <MotionDiv
                  key={role.name}
                  className="card"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={
                    inView
                      ? { opacity: 1, scale: 1 }
                      : { opacity: 0, scale: 0.9 }
                  }
                  transition={{ duration: 0.4 }}
                  onClick={() => handleSpecialRoleView(role.name)}
                >
                  <div className="text-accent font-bold text-xl mb-2">
                    {role.name}
                  </div>
                  <div className="flex justify-center mb-4">
                    {role.diceNumbers.map((num, index) => (
                      <motion.img
                        key={index}
                        src={`/images/dice${num}.png`}
                        alt={`サイコロの目${num}`}
                        className="w-10 h-10 mx-1"
                        initial={{ scale: 0.8, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                      />
                    ))}
                  </div>
                  <p className="text-gray-300 text-sm mb-3">
                    {role.description}
                  </p>
                  <div
                    className={`text-2xl font-bold ${
                      role.isNegative
                        ? "text-red-500"
                        : "bg-gradient-to-r from-accent to-purple-500 bg-clip-text text-transparent"
                    }`}
                  >
                    {role.multiplier}
                  </div>
                </MotionDiv>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Rules;
